import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { FiArrowLeft, FiUser, FiLock, FiInfo, FiDroplet, FiEye, FiEyeOff, FiBell, FiMail, FiCheck, FiX, FiTrash2, FiPlay, FiDownload } from 'react-icons/fi';
import { useTutorial } from '../context/TutorialContext';
import { Bounce } from 'react-toastify';
import { toast } from '../utils/customToast';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import DeleteAccountModal from '../components/modals/DeleteAccountModal';
import { useAuth } from '../context/AuthContext';
import 'react-toastify/dist/ReactToastify.css';
import usePageTitle from '../hooks/usePageTitle';

const COLOR_THEMES = [
    { value: 'default', label: 'Par défaut', color: '#000000', darkColor: '#ffffff', description: 'Noir et blanc classique' },
    { value: 'green', label: 'Vert', color: '#059669', darkColor: '#34d399', description: 'Thème écologique' },
    { value: 'blue', label: 'Bleu', color: '#2563eb', darkColor: '#60a5fa', description: 'Thème professionnel' },
    { value: 'red', label: 'Rouge', color: '#dc2626', darkColor: '#f87171', description: 'Thème énergique' }
];

const Profile = () => {
    usePageTitle('Mon profil');
    const { user, updateUser, getAuthHeaders, activeTenantId } = useAuth();
    const navigate = useNavigate();
    const { restart: restartTutorial, notifyAction: tutorialNotify } = useTutorial();
    const [name, setName] = useState(user?.name || '');
    const [theme, setTheme] = useState(user?.theme || 'system');
    const [colorTheme, setColorTheme] = useState(user?.colorTheme || 'default');
    const [emailNotifications, setEmailNotifications] = useState(user?.emailNotifications !== false);
    const [oldPassword, setOldPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showOldPassword, setShowOldPassword] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [oauthPassword, setOauthPassword] = useState('');
    const [oauthPasswordConfirm, setOauthPasswordConfirm] = useState('');
    const [showOauthPassword, setShowOauthPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [searchParams, setSearchParams] = useSearchParams();
    const [showDeleteModal, setShowDeleteModal] = useState(false);

    const isLocalAccount = user?.authProvider === 'local';
    const isDarkMode = document.documentElement.classList.contains('dark');

    // Vérifier si le tenant actif a un plan Business ou Enterprise
    const currentTenant = user?.tenants?.find(t =>
        t.tenantId?.toString() === activeTenantId?.toString() ||
        t._id?.toString() === activeTenantId?.toString()
    );
    const tenantPlan = currentTenant?.plan || 'free';
    const hasEmailNotifications = tenantPlan === 'business' || tenantPlan === 'enterprise';

    // Source de vérité unique pour l'onglet actif : l'URL.
    // Supprimer le useState + useEffect de sync évite le flash entre onglets
    // (le state dupliqué causait un re-render intermédiaire avec l'ancienne valeur).
    const activeSection = searchParams.get('tab') || 'info';

    // Mettre à jour l'URL lors d'un changement d'onglet
    const handleTabChange = (section) => {
        setSearchParams({ tab: section });
    };

    useEffect(() => {
        tutorialNotify('visited-profile');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const sections = [
        { id: 'info', label: 'Informations', icon: FiInfo, show: true },
        { id: 'profile', label: 'Profil', icon: FiUser, show: true },
        { id: 'appearance', label: 'Apparence', icon: FiDroplet, show: true },
        { id: 'notifications', label: 'Notifications', icon: FiBell, show: hasEmailNotifications },
        { id: 'security', label: 'Sécurité', icon: FiLock, show: true },
    ].filter(s => s.show);

    const handleUpdateProfile = async (e) => {
        e.preventDefault();
        setLoading(true);

        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/auth/me`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    ...getAuthHeaders()
                },
                body: JSON.stringify({ name, theme, colorTheme, emailNotifications })
            });

            if (response.ok) {
                const updatedUser = await response.json();
                updateUser(updatedUser);
                window.dispatchEvent(new CustomEvent('userProfileUpdated'));

                toast.success('Profil mis à jour avec succès !', {
                    position: "top-right",
                    autoClose: 5000,
                    theme: "light",
                    transition: Bounce,
                });
            } else {
                const error = await response.json();
                toast.error(error.message || 'Erreur lors de la mise à jour', {
                    position: "top-right",
                    autoClose: 5000,
                    theme: "light",
                    transition: Bounce,
                });
            }
        } catch (error) {
            toast.error('Erreur réseau. Vérifiez votre connexion.', {
                position: "top-right",
                autoClose: 5000,
                theme: "light",
                transition: Bounce,
            });
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteAccount = async (password) => {
        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/auth/me`, {
                method: 'DELETE',
                headers: {
                    'Content-Type': 'application/json',
                    ...getAuthHeaders()
                },
                body: JSON.stringify({ password })
            });

            if (response.ok) {
                toast.success('Compte supprimé avec succès', {
                    position: "top-right",
                    autoClose: 3000,
                    theme: "light",
                    transition: Bounce,
                });

                // Déconnecter et rediriger
                setTimeout(() => {
                    localStorage.removeItem('token');
                    localStorage.removeItem('user');
                    navigate('/');
                    window.location.reload();
                }, 1000);
            } else {
                const error = await response.json();
                toast.error(error.message || 'Erreur lors de la suppression du compte', {
                    position: "top-right",
                    autoClose: 5000,
                    theme: "light",
                    transition: Bounce,
                });
            }
        } catch (error) {
            console.error('Erreur:', error);
            toast.error('Erreur lors de la suppression du compte', {
                position: "top-right",
                autoClose: 5000,
                theme: "light",
                transition: Bounce,
            });
        }
    };

    const handleChangePassword = async (e) => {
        e.preventDefault();

        // Validation stricte du mot de passe
        if (newPassword.length < 8) {
            toast.error('Le mot de passe doit contenir au moins 8 caractères', {
                position: "top-right",
                autoClose: 5000,
                theme: "light",
                transition: Bounce,
            });
            return;
        }

        if (!/[a-zA-Z]/.test(newPassword)) {
            toast.error('Le mot de passe doit contenir au moins une lettre', {
                position: "top-right",
                autoClose: 5000,
                theme: "light",
                transition: Bounce,
            });
            return;
        }

        if (!/[0-9]/.test(newPassword)) {
            toast.error('Le mot de passe doit contenir au moins un chiffre', {
                position: "top-right",
                autoClose: 5000,
                theme: "light",
                transition: Bounce,
            });
            return;
        }

        if (newPassword !== confirmPassword) {
            toast.error('Les mots de passe ne correspondent pas', {
                position: "top-right",
                autoClose: 5000,
                theme: "light",
                transition: Bounce,
            });
            return;
        }

        setLoading(true);

        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/auth/change-password`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    ...getAuthHeaders()
                },
                body: JSON.stringify({ oldPassword, newPassword })
            });

            if (response.ok) {
                toast.success('Mot de passe modifié avec succès !', {
                    position: "top-right",
                    autoClose: 5000,
                    theme: "light",
                    transition: Bounce,
                });
                setOldPassword('');
                setNewPassword('');
                setConfirmPassword('');
            } else {
                const error = await response.json();
                toast.error(error.message || 'Erreur lors du changement de mot de passe', {
                    position: "top-right",
                    autoClose: 5000,
                    theme: "light",
                    transition: Bounce,
                });
            }
        } catch (error) {
            toast.error('Erreur réseau. Vérifiez votre connexion.', {
                position: "top-right",
                autoClose: 5000,
                theme: "light",
                transition: Bounce,
            });
        } finally {
            setLoading(false);
        }
    };

    const handleSetPassword = async (e) => {
        e.preventDefault();
        if (oauthPassword.length < 8 || !/[a-zA-Z]/.test(oauthPassword) || !/[0-9]/.test(oauthPassword)) {
            toast.error('Mot de passe invalide. 8+ caractères, lettres et chiffres requis.', { position: 'top-right', autoClose: 5000, transition: Bounce });
            return;
        }
        if (oauthPassword !== oauthPasswordConfirm) {
            toast.error('Les mots de passe ne correspondent pas', { position: 'top-right', autoClose: 5000, transition: Bounce });
            return;
        }
        setLoading(true);
        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/auth/set-password`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
                body: JSON.stringify({ password: oauthPassword })
            });
            if (response.ok) {
                toast.success('Mot de passe défini ! Vous pouvez maintenant vous connecter via email/mot de passe sur l\'admin.', { position: 'top-right', autoClose: 6000, transition: Bounce });
                setOauthPassword('');
                setOauthPasswordConfirm('');
            } else {
                const error = await response.json();
                toast.error(error.message || 'Erreur lors de la définition du mot de passe', { position: 'top-right', autoClose: 5000, transition: Bounce });
            }
        } catch {
            toast.error('Erreur réseau. Vérifiez votre connexion.', { position: 'top-right', autoClose: 5000, transition: Bounce });
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex flex-col bg-gray-100 dark:bg-gray-900">
            <Header />
            <main className="flex-1 pb-8">
                <div className="container mx-auto px-4 pt-4 sm:pt-8">
                    {/* Back Button */}
                    <button
                        onClick={() => navigate('/messages')}
                        className="flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-black dark:hover:text-white mb-4 sm:mb-6"
                    >
                        <FiArrowLeft /> Retour aux messages
                    </button>

                    {/* Title */}
                    <h1 className="text-xl sm:text-2xl font-bold mb-4 sm:mb-6 text-black dark:text-white flex items-center gap-2">
                        <FiUser className="hidden sm:inline" /> Mon profil
                    </h1>

                    {/* Mobile Section Selector */}
                    <div className="lg:hidden mb-4 bg-white dark:bg-gray-800 rounded-lg shadow p-2">
                        <select
                            value={activeSection}
                            onChange={(e) => handleTabChange(e.target.value)}
                            className="w-full p-3 border dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded focus:ring-2 focus:ring-primary focus:border-primary"
                        >
                            {sections.map(section => (
                                <option key={section.id} value={section.id}>
                                    {section.label}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 sm:gap-6">
                        {/* Desktop Sidebar */}
                        <div className="hidden lg:block lg:col-span-1">
                            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 sticky top-24">
                                <nav className="space-y-1">
                                    {sections.map(section => {
                                        const Icon = section.icon;
                                        return (
                                            <button
                                                key={section.id}
                                                onClick={() => handleTabChange(section.id)}
                                                className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition ${activeSection === section.id
                                                        ? 'bg-primary text-white dark:text-black'
                                                        : 'hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300'
                                                    }`}
                                            >
                                                <Icon className="h-5 w-5" />
                                                <span className="font-medium">{section.label}</span>
                                            </button>
                                        );
                                    })}
                                </nav>
                            </div>
                        </div>

                        {/* Content */}
                        <div className="lg:col-span-3 space-y-4 sm:space-y-6">
                            {/* Section Informations */}
                            {activeSection === 'info' && (
                                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg overflow-hidden">
                                    <div className="p-6">
                                        <div className="flex items-center gap-3">
                                            <div className="p-3 bg-gray-100 dark:bg-white/20 rounded-lg backdrop-blur-sm">
                                                <FiInfo className="h-6 w-6 text-black dark:text-white" />
                                            </div>
                                            <div>
                                                <h2 className="text-xl font-bold text-black dark:text-white">
                                                    Informations du compte
                                                </h2>
                                                <p className="text-gray-600 dark:text-gray-300 text-sm mt-1">
                                                    Gérez vos informations personnelles et de compte
                                                </p>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="space-y-6 p-6">
                                        <div className="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                                <div>
                                                    <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                                                        Email
                                                    </label>
                                                    <p className="text-sm font-semibold text-black dark:text-white">{user?.email}</p>
                                                </div>
                                                <div>
                                                    <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                                                        Type de compte
                                                    </label>
                                                    <p className="text-sm font-semibold text-black dark:text-white">
                                                        {user?.authProvider === 'local' ? 'Local' : user?.authProvider?.toUpperCase()}
                                                    </p>
                                                </div>
                                                <div>
                                                    <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                                                        Membre depuis
                                                    </label>
                                                    <p className="text-sm font-semibold text-black dark:text-white">
                                                        {new Date(user?.createdAt).toLocaleDateString('fr-FR')}
                                                    </p>
                                                </div>
                                                <div>
                                                    <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                                                        Organisations
                                                    </label>
                                                    <p className="text-sm font-semibold text-black dark:text-white">
                                                        {user?.tenants?.length || 0} organisation(s)
                                                    </p>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Export RGPD */}
                                        <div className="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg flex items-center justify-between gap-4">
                                            <div>
                                                <p className="text-sm font-semibold text-black dark:text-white mb-0.5">Exporter mes données (RGPD)</p>
                                                <p className="text-xs text-gray-500 dark:text-gray-400">Téléchargez une copie de toutes vos données personnelles au format JSON.</p>
                                            </div>
                                            <button
                                                onClick={async () => {
                                                    try {
                                                        const res = await fetch(`${process.env.REACT_APP_API_URL}/auth/me/export-data`, { headers: getAuthHeaders() });
                                                        if (!res.ok) throw new Error();
                                                        const blob = await res.blob();
                                                        const url = URL.createObjectURL(blob);
                                                        const a = document.createElement('a');
                                                        a.href = url;
                                                        a.download = 'mes-donnees-pulsecio.json';
                                                        a.click();
                                                        URL.revokeObjectURL(url);
                                                    } catch {
                                                        toast.error('Erreur lors de l\'export', { position: 'top-right', autoClose: 4000, transition: Bounce });
                                                    }
                                                }}
                                                className="flex items-center gap-2 flex-shrink-0 px-4 py-2 text-sm font-semibold bg-gray-200 dark:bg-gray-600 text-black dark:text-white rounded-lg transition hover:bg-gray-300 dark:hover:bg-gray-500"
                                            >
                                                <FiDownload className="h-3.5 w-3.5" />
                                                Télécharger
                                            </button>
                                        </div>

                                        {/* Tutoriel */}
                                        <div className="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg flex items-center justify-between gap-4">
                                            <div>
                                                <p className="text-sm font-semibold text-black dark:text-white mb-0.5">Tutoriel de prise en main</p>
                                                <p className="text-xs text-gray-500 dark:text-gray-400">Relancez le tutoriel interactif pour decouvrir ou redecouvrir les fonctionnalites.</p>
                                            </div>
                                            <button
                                                onClick={() => { restartTutorial(); navigate('/messages'); }}
                                                className="flex items-center gap-2 flex-shrink-0 px-4 py-2 text-sm font-semibold text-black dark:text-white rounded-lg transition hover:opacity-90"
                                                style={{ background: 'var(--color-primary, #7c3aed)' }}
                                            >
                                                <FiPlay className="h-3.5 w-3.5 text-black dark:text-white" />
                                                Revoir le tutoriel
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Section Profil */}
                            {activeSection === 'profile' && (
                                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg overflow-hidden">
                                    <div className="p-6">
                                        <div className="flex items-center gap-3">
                                            <div className="p-3 bg-gray-100 dark:bg-white/20 rounded-lg backdrop-blur-sm">
                                                <FiUser className="h-6 w-6 text-black dark:text-white" />
                                            </div>
                                            <div>
                                                <h2 className="text-xl font-bold text-black dark:text-white">
                                                    Profil
                                                </h2>
                                                <p className="text-gray-600 dark:text-gray-300 text-sm mt-1">
                                                    Gérez votre profil et vos informations personnelles
                                                </p>
                                            </div>
                                        </div>
                                    </div>

                                    <form onSubmit={handleUpdateProfile} className="space-y-6 p-6">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                                Nom complet
                                            </label>
                                            <input
                                                type="text"
                                                value={name}
                                                onChange={(e) => setName(e.target.value)}
                                                className="w-full p-3 border dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded focus:ring-2 focus:ring-primary focus:border-primary"
                                                required
                                                disabled={loading}
                                                placeholder="Votre nom"
                                                maxLength={30}
                                            />
                                            <div className="text-xs text-gray-500 pt-1">{name.trim().length}/30</div>
                                        </div>

                                        <button
                                            type="submit"
                                            disabled={loading || name.trim() === '' || name.trim().length > 30}
                                            className="w-full bg-primary text-white font-semibold dark:text-black p-3 rounded hover:bg-primary-hover transition disabled:bg-gray-400 dark:disabled:bg-gray-600"
                                        >
                                            {loading ? 'Mise à jour...' : 'Mettre à jour le profil'}
                                        </button>
                                    </form>

                                    {/* Zone de danger */}
                                    <div className="p-6 border-t dark:border-gray-700">
                                        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-6">
                                            <div className="flex items-start gap-4">
                                                <div className="p-3 bg-red-100 dark:bg-red-900/40 rounded-lg">
                                                    <FiTrash2 className="h-6 w-6 text-red-600 dark:text-red-400" />
                                                </div>
                                                <div className="flex-1">
                                                    <h3 className="text-lg font-bold text-red-900 dark:text-red-200 mb-2">
                                                        Zone de danger
                                                    </h3>
                                                    <p className="text-sm text-red-700 dark:text-red-300 mb-4">
                                                        Une fois votre compte supprimé, toutes vos données seront définitivement perdues. Cette action est irréversible.
                                                    </p>
                                                    <button
                                                        type="button"
                                                        onClick={() => setShowDeleteModal(true)}
                                                        className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition font-medium"
                                                    >
                                                        <FiTrash2 className="h-4 w-4" />
                                                        Supprimer mon compte
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Section Apparence */}
                            {activeSection === 'appearance' && (
                                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg overflow-hidden">
                                    <div className="p-6">
                                        <div className="flex items-center gap-3">
                                            <div className="p-3 bg-gray-100 dark:bg-white/20 rounded-lg backdrop-blur-sm">
                                                <FiDroplet className="h-6 w-6 text-black dark:text-white" />
                                            </div>
                                            <div>
                                                <h2 className="text-xl font-bold text-black dark:text-white">
                                                    Apparence et Thème
                                                </h2>
                                                <p className="text-gray-600 dark:text-gray-300 text-sm mt-1">
                                                    Gérez l'apparence de votre compte
                                                </p>
                                            </div>
                                        </div>
                                    </div>

                                    <form onSubmit={handleUpdateProfile} className="space-y-6 p-6">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                                Thème
                                            </label>
                                            <select
                                                value={theme}
                                                onChange={(e) => setTheme(e.target.value)}
                                                className="w-full p-3 border dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded focus:ring-2 focus:ring-primary focus:border-primary"
                                                disabled={loading}
                                            >
                                                <option value="system">Système (automatique)</option>
                                                <option value="light">Clair</option>
                                                <option value="dark">Sombre</option>
                                            </select>
                                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                                                {theme === 'system' && '💡 Le thème suivra automatiquement les préférences de votre système'}
                                                {theme === 'light' && '☀️ Mode clair activé en permanence'}
                                                {theme === 'dark' && '🌙 Mode sombre activé en permanence'}
                                            </p>
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                                                Palette de couleurs
                                            </label>
                                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                                {COLOR_THEMES.map((themeOption) => {
                                                    const isActive = colorTheme === themeOption.value;
                                                    const themeColor = isDarkMode ? themeOption.darkColor : themeOption.color;

                                                    return (
                                                        <button
                                                            key={themeOption.value}
                                                            type="button"
                                                            onClick={() => setColorTheme(themeOption.value)}
                                                            disabled={loading}
                                                            className={`flex items-center gap-3 p-4 border-2 rounded-lg transition ${isActive
                                                                    ? 'border-primary bg-primary/5 dark:bg-primary/10'
                                                                    : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                                                                }`}
                                                        >
                                                            <div
                                                                className="w-10 h-10 rounded-lg flex-shrink-0"
                                                                style={{ backgroundColor: themeColor }}
                                                            />
                                                            <div className="text-left flex-1 min-w-0">
                                                                <p className="font-semibold text-sm text-black dark:text-white">
                                                                    {themeOption.label}
                                                                </p>
                                                                <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                                                                    {themeOption.description}
                                                                </p>
                                                            </div>
                                                            {isActive && (
                                                                <div className="w-5 h-5 bg-primary rounded-full flex items-center justify-center flex-shrink-0">
                                                                    <svg className="w-3 h-3 text-white dark:text-black" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                                                    </svg>
                                                                </div>
                                                            )}
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                        </div>

                                        <button
                                            type="submit"
                                            disabled={loading}
                                            className="w-full bg-primary text-white font-semibold dark:text-black p-3 rounded hover:bg-primary-hover transition disabled:bg-gray-400 dark:disabled:bg-gray-600"
                                        >
                                            {loading ? 'Mise à jour...' : 'Enregistrer les modifications'}
                                        </button>
                                    </form>
                                </div>
                            )}

                            {/* Section Notifications */}
                            {activeSection === 'notifications' && hasEmailNotifications && (
                                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg overflow-hidden">
                                    <div className="p-6">
                                        <div className="flex items-center gap-3">
                                            <div className="p-3 bg-gray-100 dark:bg-white/20 rounded-lg backdrop-blur-sm">
                                                <FiBell className="h-6 w-6 text-black dark:text-white" />
                                            </div>
                                            <div>
                                                <h2 className="text-xl font-bold text-black dark:text-white">
                                                    Notifications Email
                                                </h2>
                                                <p className="text-gray-600 dark:text-gray-300 text-sm mt-1">
                                                    Gérez vos préférences de notifications
                                                </p>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="p-6 space-y-6">
                                        {/* Toggle principal avec animation */}
                                        <div className="relative group">
                                            <div className={`rounded-xl border-2 transition-all duration-300 ${emailNotifications
                                                    ? 'border-green-500 dark:border-green-400 bg-green-50 dark:bg-green-900/20'
                                                    : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800'
                                                }`}>
                                                <div className="p-5">
                                                    <div className="flex items-start gap-4">
                                                        {/* Toggle Switch Custom */}
                                                        <button
                                                            type="button"
                                                            onClick={() => setEmailNotifications(!emailNotifications)}
                                                            disabled={loading}
                                                            className={`relative inline-flex h-8 w-14 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-300 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 ${emailNotifications
                                                                    ? 'bg-green-500 dark:bg-green-600'
                                                                    : 'bg-gray-300 dark:bg-gray-600'
                                                                } ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
                                                            aria-label="Toggle email notifications"
                                                        >
                                                            <span className={`inline-block h-7 w-7 transform rounded-full bg-white shadow-lg ring-0 transition duration-300 ease-in-out ${emailNotifications ? 'translate-x-6' : 'translate-x-0'
                                                                }`}>
                                                                {emailNotifications ? (
                                                                    <FiCheck className="h-7 w-7 p-1.5 text-green-500" />
                                                                ) : (
                                                                    <FiX className="h-7 w-7 p-1.5 text-gray-400" />
                                                                )}
                                                            </span>
                                                        </button>

                                                        {/* Description */}
                                                        <div className="flex-1">
                                                            <label
                                                                htmlFor="emailNotifications"
                                                                className="flex items-center gap-2 text-base font-semibold text-gray-900 dark:text-white cursor-pointer"
                                                            >
                                                                <FiMail className="h-5 w-5 text-gray-500 dark:text-gray-400" />
                                                                Notifications par email
                                                            </label>
                                                            <p className="text-sm text-gray-600 dark:text-gray-400 mt-2 leading-relaxed">
                                                                Recevez un email instantané lorsque quelqu'un vous mentionne
                                                                avec <span className="font-mono bg-gray-100 dark:bg-gray-700 px-1.5 py-0.5 rounded text-xs">@votre_nom</span> dans
                                                                un message ou une tâche.
                                                            </p>
                                                            {emailNotifications && (
                                                                <div className="mt-3 flex items-center gap-2 text-sm font-medium text-green-700 dark:text-green-400">
                                                                    <div className="h-2 w-2 bg-green-500 rounded-full animate-pulse"></div>
                                                                    Notifications activées
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Types de notifications */}
                                        <div className="rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
                                            <div className="bg-gray-50 dark:bg-gray-800 px-5 py-3 border-b border-gray-200 dark:border-gray-700">
                                                <h3 className="text-sm font-bold text-gray-900 dark:text-white flex items-center gap-2">
                                                    <FiMail className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                                                    Types d'emails envoyés
                                                </h3>
                                            </div>
                                            <div className="bg-white dark:bg-gray-900/50 p-5">
                                                <ul className="space-y-3">
                                                    <li className="flex items-start gap-3 group">
                                                        <div className="flex-shrink-0 mt-0.5">
                                                            <div className="p-1.5 bg-green-100 dark:bg-green-900/30 rounded-lg">
                                                                <FiCheck className="h-4 w-4 text-green-600 dark:text-green-400" />
                                                            </div>
                                                        </div>
                                                        <div>
                                                            <p className="text-sm font-medium text-gray-900 dark:text-white">
                                                                Mentions dans les messages
                                                            </p>
                                                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                                                Soyez notifié quand quelqu'un vous mentionne dans une conversation
                                                            </p>
                                                        </div>
                                                    </li>
                                                    <li className="flex items-start gap-3 group">
                                                        <div className="flex-shrink-0 mt-0.5">
                                                            <div className="p-1.5 bg-green-100 dark:bg-green-900/30 rounded-lg">
                                                                <FiCheck className="h-4 w-4 text-green-600 dark:text-green-400" />
                                                            </div>
                                                        </div>
                                                        <div>
                                                            <p className="text-sm font-medium text-gray-900 dark:text-white">
                                                                Mentions dans les tâches
                                                            </p>
                                                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                                                Recevez une alerte lorsque vous êtes assigné à une tâche
                                                            </p>
                                                        </div>
                                                    </li>
                                                </ul>
                                            </div>
                                        </div>

                                        {/* Bouton d'action */}
                                        <button
                                            type="submit"
                                            onClick={handleUpdateProfile}
                                            disabled={loading}
                                            className="w-full bg-primary text-white font-semibold dark:text-black p-3 rounded hover:bg-primary-hover transition disabled:bg-gray-400 dark:disabled:bg-gray-600"
                                        >
                                            {loading ? 'Mise à jour...' : 'Enregistrer les modifications'}
                                        </button>
                                    </div>
                                </div>
                            )}

                            {/* Section Sécurité */}
                            {activeSection === 'security' && (
                                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg overflow-hidden">
                                    <div className="p-6">
                                        <div className="flex items-center gap-3">
                                            <div className="p-3 bg-gray-100 dark:bg-white/20 rounded-lg backdrop-blur-sm">
                                                <FiLock className="h-6 w-6 text-black dark:text-white" />
                                            </div>
                                            <div>
                                                <h2 className="text-xl font-bold text-black dark:text-white">
                                                    Sécurité du compte
                                                </h2>
                                                <p className="text-gray-600 dark:text-gray-300 text-sm mt-1">
                                                    Gérez votre mot de passe et les paramètres de sécurité
                                                </p>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Compte Google : définir un mot de passe admin */}
                                    {!isLocalAccount && (
                                        <form onSubmit={handleSetPassword} className="space-y-6 p-6">
                                            <div className="p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded">
                                                <p className="text-sm text-blue-800 dark:text-blue-300">
                                                    Votre compte est lié à Google. Vous pouvez définir un mot de passe supplémentaire pour accéder au panneau d'administration via email/mot de passe.
                                                </p>
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                                    Mot de passe admin
                                                </label>
                                                <div className="relative">
                                                    <input
                                                        type={showOauthPassword ? 'text' : 'password'}
                                                        value={oauthPassword}
                                                        onChange={(e) => setOauthPassword(e.target.value)}
                                                        className="w-full p-3 pr-10 border dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded focus:ring-2 focus:ring-primary focus:border-primary"
                                                        required
                                                        disabled={loading}
                                                        minLength={8}
                                                        placeholder="••••••••"
                                                    />
                                                    <button type="button" onClick={() => setShowOauthPassword(s => !s)}
                                                        className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700 dark:text-gray-400" disabled={loading}>
                                                        {showOauthPassword ? <FiEyeOff /> : <FiEye />}
                                                    </button>
                                                </div>
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                                    Confirmer le mot de passe
                                                </label>
                                                <div className="relative">
                                                    <input
                                                        type={showOauthPassword ? 'text' : 'password'}
                                                        value={oauthPasswordConfirm}
                                                        onChange={(e) => setOauthPasswordConfirm(e.target.value)}
                                                        className="w-full p-3 pr-10 border dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded focus:ring-2 focus:ring-primary focus:border-primary"
                                                        required
                                                        disabled={loading}
                                                        minLength={8}
                                                        placeholder="••••••••"
                                                    />
                                                </div>
                                            </div>
                                            <button type="submit" disabled={loading}
                                                className="w-full bg-primary text-white font-semibold dark:text-black p-3 rounded hover:bg-primary-hover transition disabled:bg-gray-400 dark:disabled:bg-gray-600">
                                                {loading ? 'Enregistrement...' : 'Définir le mot de passe admin'}
                                            </button>
                                        </form>
                                    )}

                                    {/* Compte local : changer le mot de passe */}
                                    {isLocalAccount && <form onSubmit={handleChangePassword} className="space-y-6 p-6">
                                        <div className="p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded">
                                            <p className="text-sm text-blue-800 dark:text-blue-300">
                                                🔒 Votre mot de passe doit contenir au moins 8 caractères, avec lettres et chiffres
                                            </p>
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                                Ancien mot de passe
                                            </label>
                                            <div className="relative">
                                                <input
                                                    type={showOldPassword ? 'text' : 'password'}
                                                    value={oldPassword}
                                                    onChange={(e) => setOldPassword(e.target.value)}
                                                    className="w-full p-3 pr-10 border dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded focus:ring-2 focus:ring-primary focus:border-primary"
                                                    required
                                                    disabled={loading}
                                                    placeholder="••••••••"
                                                />
                                                <button
                                                    type="button"
                                                    onClick={() => setShowOldPassword(s => !s)}
                                                    aria-label={showOldPassword ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
                                                    className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700 dark:text-gray-400"
                                                    disabled={loading}
                                                >
                                                    {showOldPassword ? <FiEyeOff /> : <FiEye />}
                                                </button>
                                            </div>
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                                Nouveau mot de passe
                                            </label>
                                            <div className="relative">
                                                <input
                                                    type={showNewPassword ? 'text' : 'password'}
                                                    value={newPassword}
                                                    onChange={(e) => setNewPassword(e.target.value)}
                                                    className="w-full p-3 pr-10 border dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded focus:ring-2 focus:ring-primary focus:border-primary"
                                                    required
                                                    disabled={loading}
                                                    minLength={8}
                                                    placeholder="••••••••"
                                                />
                                                <button
                                                    type="button"
                                                    onClick={() => setShowNewPassword(s => !s)}
                                                    aria-label={showNewPassword ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
                                                    className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700 dark:text-gray-400"
                                                    disabled={loading}
                                                >
                                                    {showNewPassword ? <FiEyeOff /> : <FiEye />}
                                                </button>
                                            </div>
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                                Confirmer le nouveau mot de passe
                                            </label>
                                            <div className="relative">
                                                <input
                                                    type={showConfirmPassword ? 'text' : 'password'}
                                                    value={confirmPassword}
                                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                                    className="w-full p-3 pr-10 border dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded focus:ring-2 focus:ring-primary focus:border-primary"
                                                    required
                                                    disabled={loading}
                                                    minLength={6}
                                                    placeholder="••••••••"
                                                />
                                                <button
                                                    type="button"
                                                    onClick={() => setShowConfirmPassword(s => !s)}
                                                    aria-label={showConfirmPassword ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
                                                    className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700 dark:text-gray-400"
                                                    disabled={loading}
                                                >
                                                    {showConfirmPassword ? <FiEyeOff /> : <FiEye />}
                                                </button>
                                            </div>
                                        </div>

                                        <button
                                            type="submit"
                                            disabled={loading}
                                            className="w-full bg-red-600 dark:bg-red-500 text-white p-3 rounded hover:bg-red-700 dark:hover:bg-red-600 transition disabled:bg-gray-400 dark:disabled:bg-gray-600"
                                        >
                                            {loading ? 'Changement...' : 'Changer le mot de passe'}
                                        </button>
                                    </form>}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </main>

            <DeleteAccountModal
                isOpen={showDeleteModal}
                onClose={() => setShowDeleteModal(false)}
                onConfirm={handleDeleteAccount}
                isLocalAccount={isLocalAccount}
            />

            <Footer />
        </div>
    );
};

export default Profile;